import type { ComponentProps } from 'react';
import { cn } from '@/lib/utils';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/shared/ui/tooltip';

/**
 * The surface every control on the stage sits on.
 *
 * Frosted rather than opaque, so a panel over the model still shows that there
 * is model behind it - a dentist who drags the crown under the layers panel
 * should see it go there, not have it vanish.
 */
export function FloatingPanel({ className, ...props }: ComponentProps<'div'>) {
  return (
    <div
      className={cn(
        'rounded-xl border border-white/70 bg-white/80 shadow-[0_10px_30px_-12px_rgba(15,23,42,0.28)] backdrop-blur-md',
        'dark:border-white/10 dark:bg-neutral-900/75',
        className,
      )}
      {...props}
    />
  );
}

interface StageButtonProps extends ComponentProps<'button'> {
  label: string;
  shortcut?: string;
  active?: boolean;
  side?: 'top' | 'right' | 'bottom' | 'left';
}

/**
 * An icon button on the stage, with its name on hover.
 *
 * The name is also the accessible label: an icon alone tells a screen reader
 * nothing, and "Section" is not self-evident from a pair of scissors.
 */
export function StageButton({
  label,
  shortcut,
  active = false,
  side = 'top',
  className,
  children,
  ...props
}: StageButtonProps) {
  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <button
          type="button"
          aria-label={label}
          aria-pressed={active}
          className={cn(
            'flex size-9 items-center justify-center rounded-lg text-neutral-600 transition-colors',
            'hover:bg-neutral-900/5 hover:text-neutral-900 focus-visible:ring-2 focus-visible:ring-ring focus-visible:outline-none',
            'disabled:pointer-events-none disabled:opacity-40 dark:text-neutral-300 dark:hover:bg-white/10 dark:hover:text-white',
            active &&
              'bg-primary text-primary-foreground shadow-sm hover:bg-primary hover:text-primary-foreground',
            '[&_svg]:size-[18px]',
            className,
          )}
          {...props}
        >
          {children}
        </button>
      </TooltipTrigger>
      <TooltipContent side={side} sideOffset={6}>
        {label}
        {shortcut && <kbd className="ml-2 font-mono text-[10px] opacity-70">{shortcut}</kbd>}
      </TooltipContent>
    </Tooltip>
  );
}
